// Sticky product navigation bar — reveals after scrolling past the main product,
// smooth-scrolls to in-page sections, and highlights the section in view.
// Self-contained custom element (multiple instances / Theme Editor safe).
if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('[data-psb-bar]');
        this.links = Array.from(this.querySelectorAll('[data-psb-target]'));
        this.resolveTargets();

        // The site footer is dark; the bar is fixed to the bottom, so it must
        // retract before the footer scrolls up under it (otherwise the white bar
        // overlaps the dark footer and looks clipped by a black edge).
        this.footer = document.querySelector(
          '.ryzvox-footer-wrapper, #footer-group, .shopify-section-group-footer-group, .section-footer-group'
        );

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.ticking = false;

        this.links.forEach((a) => a.addEventListener('click', (e) => this.scrollToTarget(e, a)));
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        this.measure();
        this.update();

        // Výšky sekcí se ustálí až po dotažení obrázků a videí; kdyby se odkazy počítaly jen
        // při připojení prvku, sekce, která ještě nic nezabírá, by z lišty vypadla natrvalo.
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => { this.resolveTargets(); this.update(); }, { once: true });
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
      }

      measure() {
        this.barHeight = this.bar ? this.bar.offsetHeight : 56;
        this.headerOffset = this.computeHeaderOffset();
      }

      // Height of a sticky/fixed site header so the bar (and scroll target) clears it.
      computeHeaderOffset() {
        const header = document.querySelector(
          '.shopify-section.section-header, .shopify-section.header, header.header, .header-wrapper'
        );
        if (!header) return 0;
        const cs = getComputedStyle(header);
        if (cs.position === 'sticky' || cs.position === 'fixed') {
          return Math.round(header.getBoundingClientRect().height);
        }
        return 0;
      }

      // Minimální výška, aby se sekce považovala za "na stránce". Prázdné sekce nemají 0px
      // vždycky - product-section si nechá padding, takže pár desítek pixelů zbyde i tam,
      // kde produkt data nemá.
      static MIN_SECTION_HEIGHT = 80;

      resolveElement(t) {
        if (!t) return null;
        let el = null;
        if (t[0] === '#' || t[0] === '.') {
          try { el = document.querySelector(t); } catch (e) { el = null; }
        }
        // Match the section wrapper, whose id starts with "shopify-section" and
        // ends with the section key (e.g. shopify-section-template--123__faq).
        // Scoping to shopify-section avoids matching unrelated elements that merely
        // end with the key (e.g. a header menu link "HeaderDrawer-support-help-faq").
        if (!el) el = document.querySelector('[id^="shopify-section"][id$="' + t + '"]');
        if (!el) el = document.querySelector('[id$="' + t + '"]');
        return el;
      }

      // Sekce se počítá jen tehdy, když na stránce něco zabírá. Obal sekce Shopify vykreslí
      // VŽDY, i když si její obsah produkt nezaslouží - `product-section` je podmíněná
      // metapolem, takže u příslušenství zůstane prázdný `<div>` s nulovou výškou. Odkaz na
      // něj nikam nescrolluje, což byl přesně ten rozbitý stav.
      sectionRenders(el) {
        if (!el) return false;
        if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') return false;
        return el.getBoundingClientRect().height >= ProductStickyBar.MIN_SECTION_HEIGHT;
      }

      resolveTargets() {
        const nav = this.querySelector('.psb__nav ul');
        const manual = this.links.map((a) => ({ a, el: this.resolveElement((a.getAttribute('data-psb-target') || '').trim()) }));

        // 1) Nakonfigurované odkazy, které nikam nevedou, ze lišty zmizí (schovává se celé
        //    <li>, aby po nich nezůstala mezera).
        const seen = new Set();
        const kept = [];
        manual.forEach((t) => {
          const li = t.a.closest('li') || t.a;
          if (!this.sectionRenders(t.el)) {
            li.hidden = true;
            li.style.display = 'none';
            return;
          }
          li.hidden = false;
          li.style.display = '';
          seen.add(t.el);
          kept.push(t);
        });

        // 2) Pořadí v liště = pořadí na stránce, ať se šablona a lišta nemohou rozejít.
        //
        //    Popisky se ZÁMĚRNĚ nedopočítávají z nadpisů sekcí. Zkusil jsem to a vypadalo to
        //    špatně: nadpisy jsou dlouhé a věty („Built for Real Adventures", „One bag. Five
        //    reasons."), takže se do lišty nevešly, a hlavně nejsou přeložené, zatímco popisky
        //    v šabloně se překládají přes Translations API. Odkazy jsou proto výhradně bloky
        //    sekce lišty. Nová sekce PDP tedy potřebuje i nový blok „Navigation link" -
        //    výměnou za to, že popisky jsou krátké, věcné a v jazyce zákazníka.
        if (nav) {
          kept
            .slice()
            .sort((x, y) => (x.el.compareDocumentPosition(y.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
            .forEach((t) => nav.appendChild(t.a.closest('li') || t.a));
        }

        this.links = kept.map((t) => t.a);
        this.targets = kept;
      }

      scrollToTarget(event, link) {
        const t = this.targets.find((x) => x.a === link);
        if (!t || !t.el) return; // unresolved → let the href behave normally
        event.preventDefault();
        this.measure();
        // Bar is fixed to the bottom, so only clear the sticky header at the top.
        const y = t.el.getBoundingClientRect().top + window.pageYOffset - (this.headerOffset + 12);
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      onResize() {
        this.measure();
        this.resolveTargets();
        this.update();
      }

      update() {
        // Reveal once the in-flow anchor (placed right after the product) reaches the header.
        const top = this.getBoundingClientRect().top;
        let visible = top <= this.headerOffset + 4;

        // The footer lives in the layout, parsed after this element, so it may not have
        // existed at connectedCallback — resolve it lazily here the first time it's around.
        if (!this.footer) {
          this.footer = document.querySelector(
            '.ryzvox-footer-wrapper, #footer-group, .shopify-section-group-footer-group, .section-footer-group'
          );
        }

        // Retract again once the footer rises to meet the bar, so the fixed bar
        // never overlaps the dark footer at the bottom of the page.
        if (visible && this.footer) {
          const footerTop = this.footer.getBoundingClientRect().top;
          if (footerTop <= window.innerHeight - this.barHeight + 8) visible = false;
        }

        this.classList.toggle('psb--visible', visible);

        // Highlight the link for the section currently below the sticky header.
        const probe = this.headerOffset + 24;
        let active = null;
        for (const t of this.targets) {
          if (t.el && t.el.getBoundingClientRect().top <= probe) active = t;
        }
        for (const t of this.targets) t.a.classList.toggle('is-active', t === active);
      }
    }
  );
}
